export interface SubtitleCue {
  startTime: number;
  endTime: number;
  speaker: string;
  text: string;
}

function parseTimestamp(ts: string): number {
  // SRT uses commas for ms separator (00:00:01,500), WebVTT uses dots (00:00:01.500)
  const normalized = ts.trim().replace(',', '.');
  const parts = normalized.split(':');
  if (parts.length === 3) {
    return parseFloat(parts[0]) * 3600 + parseFloat(parts[1]) * 60 + parseFloat(parts[2]);
  }
  return parseFloat(parts[0]) * 60 + parseFloat(parts[1]);
}

function extractSpeaker(text: string): { speaker: string; text: string } {
  // WebVTT voice tag: <v Name>text</v>
  const voiceMatch = text.match(/<v\s+([^>]+)>([\s\S]*?)<\/v>/i);
  if (voiceMatch) {
    return { speaker: voiceMatch[1].trim(), text: voiceMatch[2].trim() };
  }

  // SRT convention: "Name: text" (only on the first line)
  const colonMatch = text.match(/^([A-Za-z0-9_ ]+):\s+([\s\S]+)/);
  if (colonMatch) {
    return { speaker: colonMatch[1].trim(), text: colonMatch[2].trim() };
  }

  return { speaker: '', text };
}

export function parseSubtitles(text: string): SubtitleCue[] {
  const cues: SubtitleCue[] = [];
  const blocks = text.replace(/\r\n/g, '\n').split(/\n\n+/);

  for (const block of blocks) {
    const lines = block.trim().split('\n');
    const tsLineIndex = lines.findIndex(l => l.includes('-->'));
    if (tsLineIndex === -1) continue;

    const tsParts = lines[tsLineIndex].split('-->');
    if (tsParts.length !== 2) continue;

    const startTime = parseTimestamp(tsParts[0]);
    const endTime = parseTimestamp(tsParts[1]);

    const textContent = lines.slice(tsLineIndex + 1).join('\n').trim();
    const { speaker, text: cleanText } = extractSpeaker(textContent);

    if (cleanText) {
      cues.push({ startTime, endTime, speaker, text: cleanText });
    }
  }

  if (cues.length > 0) {
    console.log(`[Subtitles] Parsed ${cues.length} cues from ${blocks.length} blocks`);
  } else if (text.trim().length > 0) {
    console.warn('[Subtitles] Parsed 0 cues from non-empty subtitle text');
  }

  return cues;
}

// Keep old name as alias for backward compatibility
export const parseWebVTT = parseSubtitles;

export function getCueAtTime(cues: SubtitleCue[], time: number): SubtitleCue | null {
  for (let i = 0; i < cues.length; i++) {
    if (time >= cues[i].startTime && time < cues[i].endTime) {
      return cues[i];
    }
  }
  return null;
}
